import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { ScrollService } from '../../../../services/scroll.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PropertyManagementCompanyDataService } from '../../../../services/property-management-company/property-manager-company-data.service';
import { Organization } from '../../../../models/entities/organization';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { DetailsCardAction, DetailsCardContainerComponent } from '../../shared/cards/details-card-container/details-card-container.component';
import { ModelTypeKeys } from '../../../../enums/model-type-keys.enum';
import { GrowlerService } from '../../../../services/growler.service';
import { MatCardModule } from '@angular/material/card';
import { PropertyManagementCompanyService } from '../../../../services/property-management-company/property-management-company.service';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatListModule } from '@angular/material/list';
import { CombineLinesListDirective, LoadingListDirective } from '../../shared/directives/list.directive';
import { MatLineModule } from '@angular/material/core';
import { DecimalPipe, NgForOf } from '@angular/common';
import { OrganizationDataService } from '../../../../services/organization/organization-data.service';
import { ButtonDirective } from '../../shared/directives/button.directive';
import { RouterButtonValue } from '../../../../models/entities/router-button-value';

@UntilDestroy()
@Component({
    selector: 'app-organization-details',
    templateUrl: './organization-details.component.html',
    standalone: true,
    imports: [
        PageHeaderComponent,
        DetailsCardContainerComponent,
        MatCardModule,
        MatProgressBarModule,
        MatListModule,
        LoadingListDirective,
        MatLineModule,
        NgForOf,
        DecimalPipe,
        CombineLinesListDirective,
        ButtonDirective,
        RouterLink,
        MatButtonModule,
        MatIconModule,
    ],
})
export class OrganizationDetailsComponent implements OnInit
{
    pageTitle: string;
    routerBackButton: RouterButtonValue;
    actions: DetailsCardAction[];
    lines = 1;
    loadingInitialData = true;

    organization: Organization;
    refreshingOrganization = false;

    readonly loadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly modelType = ModelTypeKeys.ORGANIZATION;

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _organizationDataService: OrganizationDataService,
            private _activatedRoute: ActivatedRoute,
            private _router: Router,
            private _scrollService: ScrollService,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    get communityId$() {
        return this._activatedRoute.paramMap.pipe(map((params) => +params?.get('communityId')));
    }

    ngOnInit() {
        this._scrollService.scrollToTop();
        this.lines = this.getDetailActions().length;

        this.routerBackButton = {
            label: 'Back',
            route: `/pmc/${this.pmcId}/communities`,
            ariaLabel: 'Go back to communities',
        };

        this.communityId$.pipe(
                switchMap((communityId) => this.refreshOrganization(communityId)),
                untilDestroyed(this),
        ).subscribe({
            next: () => this.loadingInitialData = false,
            error: () => {
                this.loadingInitialData = false;
                this._growler.oops('Unable to load community details');
            },
        });
    }

    private refreshOrganization(orgId: number) {
        return this._propertyManagementCompanyService.getPropertyManagementOrganizations(orgId.toString()).pipe(
                map((res) => {
                    this.organization = res.data[0];
                    this.pageTitle = this.organization.name;
                    this.actions = this.getDetailActions();
                }),
        );
    }

    private goToOrganization() {
        this.loadingSubject$.next(true);

        this._organizationDataService.switchOrganization(this.organization.id).pipe(
                finalize(() => this.loadingSubject$.next(false)),
                untilDestroyed(this),
        ).subscribe({
            next: ([url, isExternalRedirect]: [string, boolean]) => {
                if (isExternalRedirect) {
                    window.location.href = url;
                } else {
                    // noinspection JSIgnoredPromiseFromCall
                    this._router.navigateByUrl(url, {skipLocationChange: true});
                }
            },
            error: () => this._growler.oops('There was a problem switching current organization.'),
        });
    }

    private getDetailActions(): DetailsCardAction[] {
        return [
            {
                icon: 'open_in_new',
                title: 'Go to Community',
                action: 'view',
                callback: () => this.goToOrganization(),
            },
        ];
    }
}
